"use client";

import * as React from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================================
// IconBadge - Styled icon containers with colored backgrounds
// ============================================================================

export interface IconBadgeProps {
  /** Icon component from lucide-react */
  icon: LucideIcon;
  /** Color variant */
  variant?: "default" | "emerald" | "blue" | "amber" | "purple" | "rose" | "slate";
  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Whether to use solid background (true) or subtle background (false) */
  solid?: boolean;
  /** Additional className */
  className?: string;
}

const variants = {
  default: {
    subtle: "bg-muted text-muted-foreground",
    solid: "bg-gray-600 text-white",
  },
  emerald: {
    subtle: "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400",
    solid: "bg-gradient-to-br from-emerald-500 to-teal-600 text-white",
  },
  blue: {
    subtle: "bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400",
    solid: "bg-gradient-to-br from-blue-500 to-indigo-600 text-white",
  },
  amber: {
    subtle: "bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400",
    solid: "bg-gradient-to-br from-amber-500 to-orange-600 text-white",
  },
  purple: {
    subtle: "bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400",
    solid: "bg-gradient-to-br from-purple-500 to-violet-600 text-white",
  },
  rose: {
    subtle: "bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400",
    solid: "bg-gradient-to-br from-rose-500 to-pink-600 text-white",
  },
  slate: {
    subtle: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400",
    solid: "bg-gradient-to-br from-slate-600 to-slate-800 text-white",
  },
};

const sizes = {
  xs: { container: "p-1 rounded", icon: "h-3 w-3" },
  sm: { container: "p-1.5 rounded-md", icon: "h-4 w-4" },
  md: { container: "p-2 rounded-lg", icon: "h-5 w-5" },
  lg: { container: "p-2.5 rounded-lg", icon: "h-6 w-6" },
  xl: { container: "p-3 rounded-xl", icon: "h-8 w-8" },
};

export function IconBadge({
  icon: Icon,
  variant = "default",
  size = "md",
  solid = false,
  className,
}: IconBadgeProps) {
  const colorStyle = variants[variant][solid ? "solid" : "subtle"];
  const sizeStyle = sizes[size];

  return (
    <div className={cn(sizeStyle.container, colorStyle, className)}>
      <Icon className={sizeStyle.icon} />
    </div>
  );
}

// ============================================================================
// IconCircle - Circular icon badge (for avatars/profiles)
// ============================================================================

export interface IconCircleProps extends Omit<IconBadgeProps, "size"> {
  size?: "sm" | "md" | "lg" | "xl";
}

const circleSizes = {
  sm: { container: "w-8 h-8", icon: "h-4 w-4" },
  md: { container: "w-10 h-10", icon: "h-5 w-5" },
  lg: { container: "w-12 h-12", icon: "h-6 w-6" },
  xl: { container: "w-16 h-16", icon: "h-8 w-8" },
};

export function IconCircle({
  icon: Icon,
  variant = "default",
  size = "md",
  solid = false,
  className,
}: IconCircleProps) {
  const colorStyle = variants[variant][solid ? "solid" : "subtle"];
  const sizeStyle = circleSizes[size];

  return (
    <div className={cn(
      "rounded-full flex items-center justify-center",
      sizeStyle.container,
      colorStyle,
      className
    )}>
      <Icon className={sizeStyle.icon} />
    </div>
  );
}

// ============================================================================
// StatusDot - Simple status indicator dot
// ============================================================================

export interface StatusDotProps {
  status: "online" | "offline" | "away" | "busy" | "success" | "error" | "warning";
  size?: "sm" | "md" | "lg";
  pulse?: boolean;
  className?: string;
}

const statusColors = {
  online: "bg-emerald-500",
  offline: "bg-gray-400",
  away: "bg-amber-500",
  busy: "bg-rose-500",
  success: "bg-emerald-500",
  error: "bg-rose-500",
  warning: "bg-amber-500",
};

const dotSizes = {
  sm: "w-2 h-2",
  md: "w-3 h-3",
  lg: "w-4 h-4",
};

export function StatusDot({
  status,
  size = "md",
  pulse = false,
  className,
}: StatusDotProps) {
  return (
    <span className={cn(
      "rounded-full inline-block",
      dotSizes[size],
      statusColors[status],
      pulse && "animate-pulse",
      className
    )} />
  );
}

